import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { downloadAdviserLogo } from "../Adviser/Utils/downloadAdviserLogo";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../DesignSystem/Sidebar";
import Breadcrumb from "../DesignSystem/Breadcrumb/Breadcrumb";
import AlertBanner from "../DesignSystem/AlertBanner/AlertBanner";
import ButtonTextLink from "../DesignSystem/Buttons/ButtonTextLink";
import Field from "../DesignSystem/Inputs/Field";
import Tabs from "../DesignSystem/Tabs/Tabs";
import { useUserEmail } from "../SignIn/UserEmailContext";
import { useUserPermissions } from "../SignIn/UserPermissionsContext";
import { fetchLogoUrl } from "../../context/Adviser/Utils/FetchLogo";
import { APP_ROUTES } from "../../constants/appRoutes";
import { canAccessDepartmentRoute } from "../Departments/utils/departmentConfig";
import {
  isValidStatementEmails,
  normaliseStatementEmails,
  statementRecipientEmails,
} from "../Adviser/Tabs/Rates/statementRecipients";
import GroupDetails from "./Tabs/GroupDetails/GroupDetails";
import BankStatementDetails from "./Tabs/BankStatementDetails/BankStatementDetails";
import {
  getGroupDetails,
  getGroupStatementRecipientCredentials,
  saveGroupBranding,
  saveGroupBankDetailsByIOGroupId,
  saveGroupRecords,
  uploadGroupLogo,
} from "./groupDetailsService";
import { resolveGroupStatementPassword } from "../Accounts/MonthlyPayments/utils/groupStatementPassword";
import "../../css/App.css";
import "../../css/Adviser/AdviserDirectory.css";
import "../../css/Adviser/AdviserDetailsPage.css";

const formatColumnName = (column) => String(column)
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .replace(/^Fca$/, "FCA")
  .replace(/^Vat$/, "VAT")
  .replace(/ Id$/g, " ID")
  .trim();

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toLocaleString("en-GB");
  const text = String(value);
  const dateMatch = text.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s].*)?$/);
  if (!dateMatch) return text;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? text : date.toLocaleString("en-GB");
};

const rowText = (row) => Object.values(row || {}).map((value) => String(value ?? "").toLowerCase()).join(" ");

const DETAIL_FIELDS = [
  "GroupId",
  "GroupName",
  // "IsLegalEntity",
  // "LegalEntityGroupName",
  // "ParentGroup1Name",
  // "GroupingName",
  // "DefaultEmail",
  // "DefaultWorkTelephone",
  // "DefaultMobile",
  "AddressCity",
  "AddressPostalCode",
  "CreatedDate",
  "AuditLastUpdatedTimestamp",
];

const GROUP_TABS = [
  { id: "groupDetails", label: "Group Details" },
  { id: "bankDetails", label: "Bank & Statement Details" },
];

const DEFAULT_STATEMENT_EMAIL = "accounts@rosemountfs.co.uk";

const getGroupInitials = (group = {}) =>
  String(group.GroupName || group.GroupId || "G")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("") || "G";

const getAdviserLabel = (adviser = {}) =>
  adviser.adviserName || adviser.adviserEmail || adviser.adviserId || "Adviser";

const normaliseHexColour = (value) => {
  const raw = String(value || "").trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw
      .split("")
      .map((character) => character + character)
      .join("")}`.toUpperCase();
  }
  return /^[0-9a-f]{6}$/i.test(raw) ? `#${raw.toUpperCase()}` : "";
};

const lighterHexColour = (hex, strength) =>
  `#${[1, 3, 5]
    .map((offset) =>
      Math.round(
        parseInt(hex.slice(offset, offset + 2), 16) * strength +
          255 * (1 - strength),
      )
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")
    .toUpperCase()}`;

const cssColor = (value, fallback = "var(--Brand1-PenBlue)") => {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  return raw.startsWith("#") || raw.startsWith("var(") ? raw : `#${raw}`;
};

const formatSortCode = (value = "") =>
  String(value)
    .replace(/\D/g, "")
    .slice(0, 6)
    .replace(/(\d{2})(?=\d)/g, "$1-");

const formatAccountNumber = (value = "") =>
  String(value).replace(/\D/g, "").slice(0, 8);

const validateBankDetails = (form = {}) => {
  if (form.arSortCode && !/^\d{2}-\d{2}-\d{2}$/.test(form.arSortCode)) {
    throw new Error("Sort Code must be in 00-00-00 format.");
  }

  if (form.arAccountNumber && !/^\d{8}$/.test(form.arAccountNumber)) {
    throw new Error("Account Number must be 8 digits.");
  }
};

const GroupLoadingState = ({ message = "" }) => (
  <div className="groupLoadingPanel" role="status" aria-live="polite">
    <div className="groupLoadingSpinner" aria-hidden="true" />
    <div className="Content-Small muted">{message}</div>
  </div>
);

const GroupDetailsPage = () => {
  const { userEmail } = useUserEmail();
  const { userPermissions = [] } = useUserPermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const requestedGroupId = searchParams.get("groupId");
  const requestedTab = searchParams.get("tab");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [syncMessage, setSyncMessage] = useState("");
  const [brandingMessage, setBrandingMessage] = useState("");
  const [brandingError, setBrandingError] = useState("");
  const [copiedColour, setCopiedColour] = useState("");
  const [copyError, setCopyError] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [downloadingLogo, setDownloadingLogo] = useState("");
  const [isLogoOpen, setIsLogoOpen] = useState(false);
  const logoButtonRef = useRef(null);
  const [logoDownloadError, setLogoDownloadError] = useState("");
  const [savingPrimaryColor, setSavingPrimaryColor] = useState(false);
  const [primaryColorValue, setPrimaryColorValue] = useState("");
  const [secondaryColorValue, setSecondaryColorValue] = useState("");
  const [tertiaryColorValue, setTertiaryColorValue] = useState("");
  const [editingBankDetails, setEditingBankDetails] = useState(false);
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [bankMessage, setBankMessage] = useState("");
  const [bankError, setBankError] = useState("");
  const [bankForm, setBankForm] = useState({
    arSortCode: "",
    arAccountNumber: "",
    arAccountHolder: "",
    emailCommissionStatementsTo: "",
    emailCommissionStatementsCc: "",
    statementPassword: "",
  });
  const [groupStatementRecipientCredentials, setGroupStatementRecipientCredentials] =
    useState([]);
  const [logosLoading, setLogosLoading] = useState(true);
  const [logoCache, setLogoCache] = useState(() => new Map());
  const [savedGroupRecords, setSavedGroupRecords] = useState({});
  const [selectedGroupId, setSelectedGroupId] = useState(requestedGroupId || "");
  const canEditBranding = ["Adviser Support", "Marketing", "Administrator", "Tech"]
    .some((permission) => userPermissions.includes(permission));
  const canEditBankDetails = ["Administrator", "Recruitment", "Finance Admin"]
    .some((permission) => userPermissions.includes(permission));
  const canViewBankDetails = canEditBankDetails ||
    userPermissions.includes("Tech") || userPermissions.includes("Adviser Support");
  const visibleGroupTabs = useMemo(
    () =>
      GROUP_TABS.filter(
        (tab) => tab.id !== "bankDetails" || canViewBankDetails,
      ),
    [canViewBankDetails],
  );
  const [activeGroupTab, setActiveGroupTab] = useState(
    visibleGroupTabs.some((tab) => tab.id === requestedTab)
      ? requestedTab
      : "groupDetails",
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getGroupDetails(userEmail)
      .then((result) => {
        if (!active) return;
        const loadedRows = Array.isArray(result.rows) ? result.rows : [];
        setRows(loadedRows);
        setLastUpdated(new Date());
        setSyncMessage("Loading...");
        return saveGroupRecords(loadedRows);
      })
      .then((savedRecords) => {
        if (!active || !savedRecords) return;
        setSavedGroupRecords(
          Object.fromEntries(
            savedRecords.map((record) => [String(record.ioGroupId), record]),
          ),
        );
        setSyncMessage(
          savedRecords.length
            ? ""
            : "",
        );
      })
      .catch((loadError) => {
        if (active) setError(loadError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [userEmail]);

  useEffect(() => {
    if (requestedGroupId && requestedGroupId !== selectedGroupId) {
      setSelectedGroupId(requestedGroupId);
    }
  }, [requestedGroupId, selectedGroupId]);

  useEffect(() => {
    if (!canViewBankDetails && activeGroupTab === "bankDetails") {
      setActiveGroupTab("groupDetails");
      const nextSearch = new URLSearchParams(location.search);
      nextSearch.set("tab", "groupDetails");
      navigate(`${location.pathname}?${nextSearch.toString()}`, {
        replace: true,
      });
      return;
    }

    if (
      requestedTab &&
      requestedTab !== activeGroupTab &&
      visibleGroupTabs.some((tab) => tab.id === requestedTab)
    ) {
      setActiveGroupTab(requestedTab);
    }
  }, [
    activeGroupTab,
    canViewBankDetails,
    location.pathname,
    location.search,
    navigate,
    requestedTab,
    visibleGroupTabs,
  ]);

  const filteredRows = useMemo(() => {
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return rows;
    return rows.filter((row) => {
      const text = rowText(row);
      return terms.every((term) => text.includes(term));
    });
  }, [rows, search]);

  const selectedGroup = useMemo(
    () => {
      const sourceRows = requestedGroupId ? rows : filteredRows;
      const matchedGroup = sourceRows.find(
        (row) => String(row.GroupId || "") === String(selectedGroupId || ""),
      );

      return matchedGroup || (requestedGroupId ? null : filteredRows[0]) || null;
    },
    [filteredRows, requestedGroupId, rows, selectedGroupId],
  );
  const selectedSavedGroup = selectedGroup
    ? savedGroupRecords[String(selectedGroup.GroupId || "")]
    : null;
  const groupStatementRecipientOptions = useMemo(() => {
    const options = new Map([
      [
        DEFAULT_STATEMENT_EMAIL,
        {
          value: DEFAULT_STATEMENT_EMAIL,
          label: DEFAULT_STATEMENT_EMAIL,
        },
      ],
    ]);

    (Array.isArray(selectedSavedGroup?.adviserLinks)
      ? selectedSavedGroup.adviserLinks
      : []
    ).forEach((adviserLink) => {
      const email = String(adviserLink?.adviserEmail || "").trim();
      if (!email) return;
      const name = getAdviserLabel(adviserLink);
      options.set(email, {
        value: email,
        label: name && name !== email ? `${name} | ${email}` : email,
      });
    });

    statementRecipientEmails(bankForm.emailCommissionStatementsTo).forEach(
      (email) => {
        if (!options.has(email)) {
          options.set(email, { value: email, label: email });
        }
      },
    );

    return [...options.values()];
  }, [bankForm.emailCommissionStatementsTo, selectedSavedGroup?.adviserLinks]);
  const effectiveGroupStatementPassword = useMemo(
    () => resolveGroupStatementPassword({
      configuredPassword: bankForm.statementPassword,
      statementRecipient: bankForm.emailCommissionStatementsTo,
      advisers: groupStatementRecipientCredentials,
    }).password,
    [
      bankForm.emailCommissionStatementsTo,
      bankForm.statementPassword,
      groupStatementRecipientCredentials,
    ],
  );
  const groupsReady = !loading && !logosLoading;
  const selectedLogoUrl =
    selectedSavedGroup?.logoUrl ||
    (selectedGroup?.GroupName
      ? logoCache.get(String(selectedGroup.GroupName).trim().toLowerCase())
      : "") ||
    "";
  useEffect(() => {
    setIsLogoOpen(false);
  }, [selectedGroup?.GroupId, selectedLogoUrl]);

  useEffect(() => {
    if (!isLogoOpen) return;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsLogoOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    const trigger = logoButtonRef.current;
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      trigger?.focus();
    };
  }, [isLogoOpen]);
  const selectedPrimaryColor =
    cssColor(
      primaryColorValue,
      "var(--Brand1-PenBlue)",
    );
  const primaryColourValue = normaliseHexColour(
    primaryColorValue,
  );
  const groupColours = primaryColourValue
    ? [
        { key: "primary", label: "Primary colour", value: primaryColourValue },
        {
          key: "secondary",
          label: "Secondary colour",
          value: normaliseHexColour(secondaryColorValue) || lighterHexColour(primaryColourValue, 0.7),
        },
        {
          key: "tertiary",
          label: "Tertiary colour",
          value: normaliseHexColour(tertiaryColorValue) || lighterHexColour(primaryColourValue, 0.4),
        },
      ]
    : [];

  useEffect(() => {
    setBrandingMessage("");
    setBrandingError("");
    setPrimaryColorValue(selectedSavedGroup?.primaryColor || "");
    setSecondaryColorValue(selectedSavedGroup?.secondaryColor || "");
    setTertiaryColorValue(selectedSavedGroup?.tertiaryColor || "");
  }, [selectedGroup?.GroupId, selectedSavedGroup?.primaryColor, selectedSavedGroup?.secondaryColor, selectedSavedGroup?.tertiaryColor]);

  useEffect(() => {
    const bankDetails = selectedSavedGroup?.bankDetails || {};
    setEditingBankDetails(false);
    setBankMessage("");
    setBankError("");
    setBankForm({
      arSortCode: bankDetails.arSortCode || "",
      arAccountNumber: bankDetails.arAccountNumber || "",
      arAccountHolder: bankDetails.arAccountHolder || "",
      emailCommissionStatementsTo: normaliseStatementEmails(
        bankDetails.emailCommissionStatementsTo,
      ),
      emailCommissionStatementsCc: normaliseStatementEmails(
        bankDetails.emailCommissionStatementsCc,
      ),
      statementPassword: String(bankDetails.statementPassword || "")
        .replace(/\s+/g, ""),
    });
  }, [
    selectedGroup?.GroupId,
    selectedSavedGroup?.bankDetails,
  ]);

  useEffect(() => {
    let active = true;
    const adviserLinks = Array.isArray(selectedSavedGroup?.adviserLinks)
      ? selectedSavedGroup.adviserLinks
      : [];
    setGroupStatementRecipientCredentials(
      adviserLinks.map((adviserLink) => ({
        email: adviserLink.adviserEmail || "",
        adviser: getAdviserLabel(adviserLink),
        dob: adviserLink.dob || null,
        statementPassword: "",
      })),
    );
    getGroupStatementRecipientCredentials(adviserLinks)
      .then((credentials) => {
        if (active) setGroupStatementRecipientCredentials(credentials);
      })
      .catch(() => {
        // The DOB fallback above remains available if rates cannot be loaded.
      });
    return () => {
      active = false;
    };
  }, [selectedGroup?.GroupId, selectedSavedGroup?.adviserLinks]);

  useEffect(() => {
    let alive = true;

    const names = [
      ...new Set(
        rows
          .map((row) => String(row.GroupName || "").trim())
          .filter(Boolean),
      ),
    ].filter((name) => !logoCache.has(name.toLowerCase()));

    if (!names.length) {
      setLogosLoading(false);
      return () => {
        alive = false;
      };
    }

    setLogosLoading(true);

    Promise.all(
      names.map(async (name) => [name, await fetchLogoUrl(name)]),
    ).then((entries) => {
      if (!alive) return;

      setLogoCache((current) => {
        const next = new Map(current);
        entries.forEach(([name, url]) => {
          next.set(String(name).trim().toLowerCase(), url || "");
        });
        return next;
      });
      setLogosLoading(false);
    }).catch(() => {
      if (alive) setLogosLoading(false);
    });

    return () => {
      alive = false;
    };
  }, [logoCache, rows]);

  useEffect(() => {
    if (!filteredRows.length) {
      setSelectedGroupId("");
      return;
    }

    if (
      !filteredRows.some(
        (row) => String(row.GroupId || "") === String(selectedGroupId || ""),
      )
    ) {
      setSelectedGroupId(String(filteredRows[0].GroupId || ""));
    }
  }, [filteredRows, selectedGroupId]);

  const handleSelectGroup = (groupId) => {
    const nextGroupId = String(groupId || "");
    setSelectedGroupId(nextGroupId);
  };

  const handleOpenSelectedGroup = () => {
    if (!selectedGroup?.GroupId) return;

    navigate(`/GroupDetails?groupId=${encodeURIComponent(selectedGroup.GroupId)}`, {
      replace: true,
    });
  };

  const copyGroupColour = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedColour(`${selectedGroup?.GroupId}:${key}`);
      setCopyError("");
    } catch {
      setCopyError("Could not copy the colour. Please select and copy the hex code.");
    }
  };

  const handleGroupTabChange = (tabId) => {
    if (!visibleGroupTabs.some((tab) => tab.id === tabId)) return;

    setActiveGroupTab(tabId);
    const nextSearch = new URLSearchParams(location.search);
    nextSearch.set("tab", tabId);
    navigate(`${location.pathname}?${nextSearch.toString()}`, {
      replace: true,
    });
  };

  const handleOpenAdviser = (adviser = {}) => {
    const adviserKey = adviser.adviserEmail || adviser.adviserId;
    if (!adviserKey) return;

    navigate(`/advisers/${encodeURIComponent(adviserKey)}`);
  };

  const mergeSavedGroupRecord = (ioGroupId, patch = {}) => {
    setSavedGroupRecords((current) => ({
      ...current,
      [String(ioGroupId)]: {
        ...(current[String(ioGroupId)] || {}),
        ioGroupId: String(ioGroupId),
        ...patch,
      },
    }));
  };

  const handleGroupLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file || !selectedGroup?.GroupId || !canEditBranding) return;

    try {
      setUploadingLogo(true);
      setBrandingError("");
      setBrandingMessage("");

      const branding = await uploadGroupLogo(selectedGroup, file);
      mergeSavedGroupRecord(selectedGroup.GroupId, branding);
      setBrandingMessage("Group logo uploaded.");
    } catch (uploadError) {
      setBrandingError(uploadError.message || "Group logo could not be uploaded.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDownloadLogo = async (format) => {
    setDownloadingLogo(format);
    setLogoDownloadError("");
    try {
      await downloadAdviserLogo(selectedLogoUrl, selectedGroup?.GroupName || "group", format);
    } catch {
      setLogoDownloadError("Unable to download the logo. Please try again.");
    } finally {
      setDownloadingLogo("");
    }
  };

  const handleSavePrimaryColor = async () => {
    if (!selectedGroup?.GroupId || !canEditBranding) return;

    try {
      setSavingPrimaryColor(true);
      setBrandingError("");
      setBrandingMessage("");

      const branding = await saveGroupBranding(selectedGroup.GroupId, {
        primaryColor: primaryColorValue,
        secondaryColor: secondaryColorValue,
        tertiaryColor: tertiaryColorValue,
      });
      mergeSavedGroupRecord(selectedGroup.GroupId, branding);
      setBrandingMessage("Group colours saved and applied to all advisers linked by group ID.");
    } catch (saveError) {
      setBrandingError(saveError.message || "Group colours could not be saved.");
    } finally {
      setSavingPrimaryColor(false);
    }
  };

  const updateBankField = (key, value) => {
    setBankForm((current) => ({
      ...current,
      [key]:
        key === "arSortCode"
          ? formatSortCode(value)
          : key === "arAccountNumber"
            ? formatAccountNumber(value)
            : value,
    }));
  };

  const handleSaveBankDetails = async () => {
    if (!selectedGroup?.GroupId || !canEditBankDetails) return;

    try {
      setSavingBankDetails(true);
      setBankError("");
      setBankMessage("");
      validateBankDetails(bankForm);
      if (!isValidStatementEmails(bankForm.emailCommissionStatementsTo)) {
        throw new Error(
          "To email addresses must be valid and separated by commas.",
        );
      }
      if (!isValidStatementEmails(bankForm.emailCommissionStatementsCc)) {
        throw new Error(
          "CC email addresses must be valid and separated by commas.",
        );
      }

      const saved = await saveGroupBankDetailsByIOGroupId(
        selectedGroup.GroupId,
        bankForm,
      );
      mergeSavedGroupRecord(selectedGroup.GroupId, saved);
      setEditingBankDetails(false);
      setBankMessage("Group bank and statement details saved.");
    } catch (saveError) {
      setBankError(
        saveError.message || "Group bank and statement details could not be saved.",
      );
    } finally {
      setSavingBankDetails(false);
    }
  };

  if (requestedGroupId) {
    return (
      <div className="main-content">
        <Helmet>
          <title>{selectedGroup?.GroupName || "Group"} • Group</title>
        </Helmet>
        <Sidebar />

        <div className="Main-Container">
          <div className="smProfilePage">
            <div className="smDetailsBreadcrumb">
              <Breadcrumb
                items={[
                  { label: "All Staff", to: "/Department/AllStaff" },
                  { label: "Groups", to: "/GroupDetails" },
                  { label: selectedGroup?.GroupName || requestedGroupId },
                ]}
              />
            </div>

            {error ? (
              <AlertBanner
                type="error"
                message={error}
                onClose={() => setError("")}
              />
            ) : null}

            {!groupsReady ? (
              <GroupLoadingState message="" />
            ) : (
              <>
            <div
              className="smCover profileBanner"
              style={{ background: selectedPrimaryColor }}
            >
              <div className="smCoverInner">
                <h3 className="smCoverTitle">
                  {selectedGroup?.GroupName || "Group"}
                </h3>
                <div className="smCoverSub Content-Small">
                  Group ID {selectedGroup?.GroupId || requestedGroupId}
                </div>
              </div>
              {selectedLogoUrl ? (
                <div className="profileHeaderRight">
                  <div className="profileLogoRight">
                    <img
                      className="smLogoImg-directory"
                      src={selectedLogoUrl}
                      alt={`${selectedGroup?.GroupName || "Group"} logo`}
                      loading="lazy"
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="smHeader">
              <div className="smHeaderLeft">
                <div className="smIdentity">
                  <div className="smNameRow">
                    <h2 className="smName Content-SemiBold">
                      {selectedGroup?.GroupName || "Loading group..."}
                    </h2>
                    {/* {selectedGroup?.IsLegalEntity ? (
                      <span className="smBadge">
                        Legal entity: {selectedGroup.IsLegalEntity}
                      </span>
                    ) : null} */}
                  </div>

                  <div className="smBio Content-Small">
                    Group ID {selectedGroup?.GroupId || requestedGroupId}
                    {selectedGroup?.GroupingName ? (
                      <>
                        <span className="dot"> •</span>{" "}
                        {selectedGroup.GroupingName}
                      </>
                    ) : null}
                  </div>

                  <div className="smMetaGroups">
                    <div className="smMetaGroup">
                      <div className="smMetaLabel Content-Small muted">
                        Group Branding
                      </div>
                      <div className="adviserSocialIconsRow">
                        {canEditBranding ? <label className="btn Content-Small">
                          {uploadingLogo ? "Uploading..." : "Upload logo"}
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            disabled={uploadingLogo || !selectedGroup}
                            onChange={handleGroupLogoUpload}
                          />
                        </label> : null}
                        {selectedLogoUrl ? ["png", "svg"].map((format) => (
                          <button
                            key={format}
                            type="button"
                            className="btn Content-Small"
                            disabled={Boolean(downloadingLogo) || uploadingLogo}
                            onClick={() => handleDownloadLogo(format)}
                          >
                            {downloadingLogo === format ? "Downloading..." : `Download ${format.toUpperCase()}`}
                          </button>
                        )) : null}
                        {logoDownloadError ? <span role="alert" className="Content-Small advLogoDownloadError">{logoDownloadError}</span> : null}
                        <div className="adviserColourList Content-Small">
                          <div className="adviserPrimaryColour">
                            {canEditBranding ? <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                              {[
                                ["Primary colour", primaryColorValue, setPrimaryColorValue],
                                ["Secondary colour", secondaryColorValue, setSecondaryColorValue],
                                ["Tertiary colour", tertiaryColorValue, setTertiaryColorValue],
                              ].map(([label, value, setValue], index) => {
                                const preview = normaliseHexColour(value) ||
                                  (index > 0 && !String(value).trim() && primaryColourValue
                                    ? lighterHexColour(primaryColourValue, index === 1 ? 0.7 : 0.4)
                                    : "");
                                return (
                                  <div key={label} style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                                    <Field
                                      label={label}
                                      value={value}
                                      placeholder={index === 0 ? "#081e56" : "Automatic lighter shade"}
                                      disabled={savingPrimaryColor}
                                      readOnly={!canEditBranding}
                                      onChange={setValue}
                                    />
                                    <span
                                      className="adviserPrimaryColourSwatch"
                                      style={{ backgroundColor: preview || "transparent", marginBottom: 12 }}
                                      role="img"
                                      aria-label={preview ? `${label} preview: ${preview}` : `${label}: no valid colour`}
                                      title={preview || "No valid colour"}
                                    />
                                  </div>
                                );
                              })}
                            </div> : (
                              <>
                                {groupColours.map((colour) => (
                                  <div className="adviserPrimaryColour" key={colour.key}>
                                    <span className="adviserColourLabel">{colour.label}</span>
                                    <span
                                      className="adviserPrimaryColourSwatch"
                                      style={{ backgroundColor: colour.value }}
                                      aria-hidden="true"
                                    />
                                    <b>{colour.value}</b>
                                    <button
                                      type="button"
                                      className="adviserPrimaryColourCopy"
                                      aria-label={`Copy ${colour.label.toLowerCase()} ${colour.value}`}
                                      onClick={() => copyGroupColour(colour.key, colour.value)}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </button>
                                    {copiedColour === `${selectedGroup.GroupId}:${colour.key}` ? (
                                      <span className="adviserColourCopied" role="status">Copied</span>
                                    ) : null}
                                  </div>
                                ))}
                                {!groupColours.length ? <span className="muted">No group colours saved.</span> : null}
                                {copyError ? <span role="alert">{copyError}</span> : null}
                              </>
                            )}
                            {canEditBranding ? <div style={{ flexBasis: "100%" }}><button
                              type="button"
                              className="btn Content-Small"
                              disabled={savingPrimaryColor || !selectedGroup}
                              onClick={handleSavePrimaryColor}
                            >
                              {savingPrimaryColor
                                ? "Saving..."
                                : "Save colours"}
                            </button></div> : null}
                          </div>
                        </div>
                      </div>
                      {brandingMessage ? (
                        <div className="Content-Small muted">
                          {brandingMessage}
                        </div>
                      ) : null}
                      {brandingError ? (
                        <div
                          className="Content-Small advLogoDownloadError"
                          role="alert"
                        >
                          {brandingError}
                        </div>
                      ) : null}
                    </div>

                    <div className="smMetaGroup">
                      <div className="smMetaLabel Content-Small muted">
                        Linked advisers
                      </div>
                      <div className="Content-Small">
                        {selectedSavedGroup?.adviserLinks?.length ? (
                          selectedSavedGroup.adviserLinks.map((adviser, index) => (
                            <span key={adviser.adviserId || adviser.adviserEmail}>
                              <ButtonTextLink
                                label={getAdviserLabel(adviser)}
                                onClick={() => handleOpenAdviser(adviser)}
                              />
                              {index < selectedSavedGroup.adviserLinks.length - 1
                                ? ", "
                                : ""}
                            </span>
                          ))
                        ) : (
                          <span className="muted">No linked advisers found.</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <div className="smsizedBox" />

            <Tabs
              noShadow
              tabs={visibleGroupTabs}
              activeTab={activeGroupTab}
              onTabChange={handleGroupTabChange}
            />

            <div className="smsizedBox" />

            <div className="smFeed">
              {activeGroupTab === "groupDetails" ? (
                <GroupDetails
                  selectedGroup={selectedGroup}
                  fields={DETAIL_FIELDS}
                  formatColumnName={formatColumnName}
                  formatValue={formatValue}
                />
              ) : null}

              {canViewBankDetails && activeGroupTab === "bankDetails" ? (
                <BankStatementDetails
                  selectedGroup={selectedGroup}
                  selectedSavedGroup={selectedSavedGroup}
                  canEditBankDetails={canEditBankDetails}
                  editingBankDetails={editingBankDetails}
                  setEditingBankDetails={setEditingBankDetails}
                  bankForm={bankForm}
                  effectiveStatementPassword={effectiveGroupStatementPassword}
                  updateBankField={updateBankField}
                  groupStatementRecipientOptions={groupStatementRecipientOptions}
                  savingBankDetails={savingBankDetails}
                  handleSaveBankDetails={handleSaveBankDetails}
                  bankMessage={bankMessage}
                  bankError={bankError}
                />
              ) : null}

              {/* <div className="smPost">
                <div className="smPostHeader">
                  <div className="smPostTitle Content-SemiBold">
                    Saved Branding
                  </div>
                </div>
                <div className="smGrid2">
                  <div className="smRow">
                    <div className="smRowLabel Content-Small">Logo path</div>
                    <div className="smRowValue Content">
                      {selectedSavedGroup?.logoStoragePath || "-"}
                    </div>
                  </div>
                  <div className="smRow">
                    <div className="smRowLabel Content-Small">
                      Primary colour
                    </div>
                    <div className="smRowValue Content">
                      {selectedSavedGroup?.primaryColor || "-"}
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Groups</title>
      </Helmet>
      <Sidebar isSetExpanded={true} />

      <main className="Main-Container">
        <div className="advPage">
          <Breadcrumb
            items={[
              { label: "All Staff", to: "/Department/AllStaff" },
              { label: "Groups" },
            ]}
          />

          <div className="advHeader">
            <div className="advTitle">
              <h2>Groups</h2>
              {groupsReady ? <h4>{filteredRows.length}</h4> : null}
            </div>
          </div>

          <header className="adviserDirectoryToolbar">
            <div className="fit-column gap-xs">
              {/* <p className="FP_Help_Text primary">
                View Intelliflo group records from IO.
              </p> */}
              {lastUpdated && (
                <p className="FP_Help_Text primary">
                  Last updated: {lastUpdated.toLocaleString("en-GB")}
                </p>
              )}
              {syncMessage ? (
                <p className="FP_Help_Text primary">{syncMessage}</p>
              ) : null}
            </div>
            <input
              className="advSearch Content-Small"
              id="group-details-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search group details"
            />
          </header>

          {error && (
            <AlertBanner
              type="error"
              message={error}
              onClose={() => setError("")}
            />
          )}

          {!groupsReady ? (
            <GroupLoadingState message="Loading groups..." />
          ) : (
          <main className="advGrid">
            <section className="advList">
              {filteredRows.length === 0 ? (
                <div className="advEmpty Content-Small">
                  No groups match your search.
                </div>
              ) : (
                <div className="advScroll">
                  {filteredRows.map((row) => (
                    (() => {
                      const rowRecord =
                        savedGroupRecords[String(row.GroupId || "")] || {};
                      const rowPrimaryColor = cssColor(rowRecord.primaryColor);
                      const rowLogoUrl =
                        rowRecord.logoUrl ||
                        logoCache.get(
                          String(row.GroupName || "").trim().toLowerCase(),
                        ) ||
                        "";
                      const rowAdviserNames = (rowRecord.adviserLinks || [])
                        .map(getAdviserLabel)
                        .filter(Boolean)
                        .join(", ");

                      return (
                    <button
                      key={`${row.GroupId}-${row.GroupName}`}
                      type="button"
                      className={`advRow groupDetailsRow ${
                        String(row.GroupId || "") ===
                        String(selectedGroup?.GroupId || "")
                          ? "isActive"
                          : ""
                      }`}
                      style={{
                        borderLeft: `4px solid ${rowPrimaryColor}`,
                      }}
                      onClick={() => handleSelectGroup(row.GroupId)}
                    >
                      <div
                        className="advAvatar Content-SemiBold"
                        style={{
                          background: rowPrimaryColor,
                          color: "var(--White)",
                          overflow: "hidden",
                        }}
                      >
                        {rowLogoUrl ? (
                          <img
                            src={rowLogoUrl}
                            alt={`${row.GroupName || "Group"} logo`}
                            loading="lazy"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              background: "var(--White)",
                            }}
                          />
                        ) : (
                          getGroupInitials(row)
                        )}
                      </div>

                      <div className="advRowMain">
                        <div className="advRowTop">
                          <span className="advName Content-SemiBold">
                            {row.GroupName || "Unnamed group"}
                          </span>
                          {/* {row.IsLegalEntity ? (
                            <span className="advTypeBadges">
                              <span className="badge Content-Small">
                                {row.IsLegalEntity}
                              </span>
                            </span>
                          ) : null} */}
                        </div>

                        <div className="profileMeta advSub Content-Small">
                          <span className="muted">
                            Group ID {row.GroupId || "-"}
                          </span>
                          <span className="dot">•</span>
                          <span className="muted">
                            {savedGroupRecords[
                              String(row.GroupId || "")
                            ]?.adviserLinks?.length?.toLocaleString() ||
                              "0"}{" "}
                            linked advisers
                          </span>
                          {/* {row.GroupingName ? <span className="dot">•</span> : null}
                          {row.GroupingName ? (
                            <span className="muted">{row.GroupingName}</span>
                          ) : null} */}
                        </div>

                        <div className="profileMeta advSub Content-Small">
                          <span className="muted">
                            {rowAdviserNames || "\u00A0"}
                          </span>
                        </div>

                        {/* <div className="advTags">
                          {row.ParentGroup1Name ? (
                            <span className="tag">{row.ParentGroup1Name}</span>
                          ) : null}
                          {row.LegalEntityGroupName ? (
                            <span className="tag">{row.LegalEntityGroupName}</span>
                          ) : null}
                        </div> */}
                      </div>
                    </button>
                      );
                    })()
                  ))}
                </div>
              )}
            </section>

            <section className="advProfile">
              {selectedGroup ? (
                <div className="profileWrap">
                  <div
                    className="profileBanner"
                    style={{ background: selectedPrimaryColor }}
                  >
                    <div className="profileBannerText SubTitle">
                      {selectedGroup.GroupName ||
                        selectedGroup.GroupingName ||
                        "Group"}
                    </div>

                  </div>

                  <div className="profileHeader">
                    <div className="profileHeaderLeft">
                      <h2 className="profileName Content-SemiBold">
                        {selectedGroup.GroupName || "No group selected"}
                      </h2>

                      <div className="profileMeta Content-Small">
                        <span className="muted">
                          Group ID {selectedGroup.GroupId || "-"}
                        </span>
                        <span className="dot">•</span>
                        <span className="muted">
                          {selectedSavedGroup?.adviserLinks?.length?.toLocaleString() ||
                            "0"}{" "}
                          linked advisers
                        </span>
                      </div>

                      {/* <div className="profileActionsRow">
                        <span className="badge Content-Small">
                          {Object.keys(
                            selectedSavedGroup?.bandDetails || {},
                          ).length.toLocaleString()}{" "}
                          band fields saved
                        </span>
                        {selectedGroup.IsLegalEntity ? (
                          <span className="badge Content-Small">
                            Legal entity: {selectedGroup.IsLegalEntity}
                          </span>
                        ) : null}
                      </div> */}

                      <div className="groupPreviewBranding">
                      <div className="adviserColourList Content-Small">
                        {groupColours.map((colour) => (
                          <div className="adviserPrimaryColour" key={colour.key}>
                            <span className="adviserColourLabel">{colour.label}</span>
                            <span
                              className="adviserPrimaryColourSwatch"
                              style={{ backgroundColor: colour.value }}
                              aria-hidden="true"
                            />
                            <b>{colour.value}</b>
                            <button
                              type="button"
                              className="adviserPrimaryColourCopy"
                              aria-label={`Copy ${colour.label.toLowerCase()} ${colour.value}`}
                              title={`Copy ${colour.label.toLowerCase()}`}
                              onClick={() => copyGroupColour(colour.key, colour.value)}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </button>
                            {copiedColour === `${selectedGroup.GroupId}:${colour.key}` ? (
                              <span className="adviserColourCopied" role="status">Copied</span>
                            ) : null}
                          </div>
                        ))}
                        {!groupColours.length ? <span className="muted">No group colours saved.</span> : null}
                        {copyError ? <span role="alert">{copyError}</span> : null}
                      </div>



                      </div>

                      <div className="profileActionsRow">
                        <button
                          type="button"
                          className="btn Content-Small"
                          onClick={handleOpenSelectedGroup}
                        >
                          Open group details
                        </button>
                      </div>
                    </div>
                        {selectedLogoUrl ? (
                          <button
                            ref={logoButtonRef}
                            type="button"
                            className="profileLogoRight-directory"
                            onClick={() => setIsLogoOpen(true)}
                            aria-label={`View a larger logo of ${selectedGroup.GroupName || "Group"}`}
                            title="View larger logo"
                          >
                          <img
                            className="groupPreviewLogo"
                            src={selectedLogoUrl}
                            alt={`${selectedGroup.GroupName || "Group"} logo`}
                            loading="lazy"
                          />
                          </button>
                        ) : null}
                  </div>

                  {isLogoOpen && selectedLogoUrl ? (
                    <div
                      className="adviserPhotoModal"
                      role="dialog"
                      aria-modal="true"
                      aria-label={`${selectedGroup.GroupName || "Group"} logo`}
                      onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setIsLogoOpen(false);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Tab") event.preventDefault();
                      }}
                    >
                      <div className="adviserPhotoModalContent">
                        <button
                          autoFocus
                          type="button"
                          className="adviserPhotoModalClose"
                          onClick={() => setIsLogoOpen(false)}
                          aria-label="Close logo preview"
                        >
                          &times;
                        </button>
                        <img src={selectedLogoUrl} alt={`${selectedGroup.GroupName || "Group"} logo`} />
                      </div>
                    </div>
                  ) : null}

                  <div className="profileCards">
                    <div className="card">
                      {/* <div className="Content-SemiBold">Linked advisers</div> */}
                      {/* <div className="smsizedBox" /> */}
                      {selectedSavedGroup?.adviserLinks?.length ? (
                        <div className="Content-Small">
                          {selectedSavedGroup.adviserLinks.map((adviser, index) => (
                            <span key={adviser.adviserId || adviser.adviserEmail}>
                            <ButtonTextLink
                              label={getAdviserLabel(adviser)}
                              onClick={() => handleOpenAdviser(adviser)}
                            />
                              {index < selectedSavedGroup.adviserLinks.length - 1
                                ? ", "
                                : ""}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="Content-Small muted">
                          No linked advisers found.
                        </div>
                      )}
                    </div>

                    {/* <div className="card">
                      <div className="profileActionsRow">
                        <div className="Content-SemiBold">
                          Group Bank Details
                        </div>
                        <ButtonTextLink
                          label="Edit bank details"
                          onClick={handleOpenSelectedGroup}
                        />
                      </div>
                      <div className="kv">
                        <span className="Content-Small">Sort Code</span>
                        <strong className="wrap Content">
                          {selectedSavedGroup?.bankDetails?.arSortCode || "-"}
                        </strong>
                      </div>
                      <div className="kv">
                        <span className="Content-Small">Account Number</span>
                        <strong className="wrap Content">
                          {selectedSavedGroup?.bankDetails?.arAccountNumber ||
                            "-"}
                        </strong>
                      </div>
                      <div className="kv">
                        <span className="Content-Small">Account Holder</span>
                        <strong className="wrap Content">
                          {selectedSavedGroup?.bankDetails?.arAccountHolder ||
                            "-"}
                        </strong>
                      </div>
                    </div> */}

                    <div className="card">
                      {DETAIL_FIELDS.map((field) => (
                        <div key={field} className="kv">
                          <span className="Content-Small">
                            {formatColumnName(field)}
                          </span>
                          <strong className="wrap Content">
                            {formatValue(selectedGroup[field]) || "-"}
                          </strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </section>
          </main>
          )}
        </div>
      </main>
    </div>
  );
};

const ProtectedGroupDetailsPage = () => {
  const { userPermissions = [] } = useUserPermissions();
  const canAccess = canAccessDepartmentRoute(
    userPermissions,
    APP_ROUTES.GROUP_DETAILS,
  );

  return canAccess
    ? <GroupDetailsPage />
    : <Navigate to={APP_ROUTES.DEPARTMENT_ALL_STAFF} replace />;
};

export default ProtectedGroupDetailsPage;
